/**
 * The listeners behind right-mouse free look. Everything decision-shaped —
 * sensitivity, clamps, the return easing — is `free-look.ts`'s and is unit
 * tested; this file is four handlers, one mutable state reference and the
 * pointer capture.
 *
 * The same split, and the same window-level placement, as `pointer.ts`: the
 * *press* is filtered through `isViewportPointerTarget` so a right-click on
 * the HUD, the PREFERENCES button or the exit link never starts a drag, while
 * the *move* and the *release* are taken from anywhere, because a look that
 * stuck at 150° because the pointer crossed a HUD chip would be worse than one
 * that never started.
 *
 * ## Pointer capture, and why the release still has a belt and braces
 *
 * `setPointerCapture` retargets every subsequent event for that pointer id at
 * the element that took it, so a drag that leaves the window keeps steering the
 * view and still delivers its `pointerup` — which is the difference between a
 * head that comes back and one stuck looking over its shoulder. It is not
 * enough on its own: a capture can be lost (`lostpointercapture`, when the
 * element is removed — the canvas host is replaced on an ENHANCED MODELS
 * toggle), the pointer can be cancelled by the OS, and `blur` can arrive with
 * no pointer event at all. All four release.
 *
 * ## It shares a button with the context menu, and that is fine
 *
 * `shell/context-menu.ts` suppresses `contextmenu` at the document root in the
 * capture phase. The two are different events: suppressing the menu is a
 * `preventDefault()` on `contextmenu`, which neither stops nor consumes the
 * `pointerdown`/`pointermove`/`pointerup` sequence this file listens to. The
 * ordering has no effect either way, and `free-look.test.ts` drives both
 * together to keep it that way — without the suppression the native menu takes
 * the pointer mid-drag and the `pointerup` never arrives.
 */

import { classChainOf, isViewportPointerTarget } from "./keys.ts";
import {
  FREE_LOOK_AT_REST,
  freeLookAdvance,
  freeLookDrag,
  freeLookFrameOf,
  freeLookPress,
  freeLookRelease,
  type FreeLookFrame,
  type FreeLookState,
} from "./free-look.ts";

/** The right-hand button. `pointer.ts`'s `PRIMARY_BUTTON` (0) is the trigger; this is the only other one the flight uses. */
const SECONDARY_BUTTON = 2;

export interface FreeLookControls {
  /**
   * Advances the eased return by `dtSeconds` and reports where the head is
   * pointed. **Mutating**, like `PointerControls.frame` — the scene calls it
   * exactly once per rendered frame.
   */
  frame(dtSeconds: number): FreeLookFrame;
  dispose(): void;
}

/** The one DOM capability this module needs off an event target, named so the guard below reads as a check and not a cast. */
interface PointerCapturing {
  setPointerCapture(pointerId: number): void;
  releasePointerCapture(pointerId: number): void;
}

/**
 * The live capture, as a two-call object: which pointer the drag owns and which
 * element is holding it. Split out of `createFreeLookControls` because it is the
 * one piece of this file with state of its own that is not the aim — and because
 * both of its calls can throw for reasons that mean "carry on regardless".
 */
function pointerCapture() {
  let element: PointerCapturing | null = null;
  let pointerId: number | undefined;
  return {
    get pointerId(): number | undefined {
      return pointerId;
    },
    /** Takes the capture if the target will give it; the drag runs either way, on the window listeners alone. */
    take(target: EventTarget | null, id: number): void {
      pointerId = id;
      const candidate = target as Partial<PointerCapturing> | null;
      const capturing =
        candidate && typeof candidate.setPointerCapture === "function" && typeof candidate.releasePointerCapture === "function";
      element = capturing ? (candidate as PointerCapturing) : null;
      try {
        element?.setPointerCapture(id);
      } catch {
        element = null;
      }
    },
    release(): void {
      try {
        if (pointerId !== undefined) element?.releasePointerCapture(pointerId);
      } catch {
        // Already released — by the browser on `pointerup`, or along with the
        // element that took it. Nothing to undo, and nothing worth reporting.
      }
      element = null;
      pointerId = undefined;
    },
  };
}

export function createFreeLookControls(): FreeLookControls {
  let state: FreeLookState = FREE_LOOK_AT_REST;
  const capture = pointerCapture();
  // The drag is measured from our own last position rather than from
  // `movementX`/`movementY`: those are reported in physical device pixels on
  // some platforms and scale with the OS pointer acceleration on others, and a
  // sensitivity quoted in degrees per CSS pixel has to be measured in CSS
  // pixels to mean anything.
  let lastClientX = 0;
  let lastClientY = 0;

  function onPointerDown(event: PointerEvent): void {
    if (event.button !== SECONDARY_BUTTON) return;
    if (!isViewportPointerTarget(classChainOf(event.target))) return;
    capture.release();
    state = freeLookPress(state);
    lastClientX = event.clientX;
    lastClientY = event.clientY;
    capture.take(event.target, event.pointerId);
  }

  function onPointerMove(event: PointerEvent): void {
    if (!state.dragging || event.pointerId !== capture.pointerId) return;
    state = freeLookDrag(state, event.clientX - lastClientX, event.clientY - lastClientY);
    lastClientX = event.clientX;
    lastClientY = event.clientY;
  }

  function onRelease(): void {
    state = freeLookRelease(state);
    capture.release();
  }

  function onPointerUp(event: PointerEvent): void {
    // `pointercancel` carries no meaningful `button`, so only a real
    // `pointerup` is filtered on it.
    if (event.pointerId !== capture.pointerId) return;
    if (event.type === "pointerup" && event.button !== SECONDARY_BUTTON) return;
    onRelease();
  }

  const listeners = [
    ["pointerdown", onPointerDown],
    ["pointermove", onPointerMove],
    ["pointerup", onPointerUp],
    ["pointercancel", onPointerUp],
    ["lostpointercapture", onRelease],
    ["blur", onRelease],
  ] as const;
  for (const [type, handler] of listeners) window.addEventListener(type, handler as EventListener);

  return {
    frame(dtSeconds: number): FreeLookFrame {
      state = freeLookAdvance(state, dtSeconds);
      return freeLookFrameOf(state);
    },
    dispose() {
      for (const [type, handler] of listeners) window.removeEventListener(type, handler as EventListener);
      capture.release();
      state = FREE_LOOK_AT_REST;
    },
  };
}
